import { useCallback, useEffect, useRef, useState } from 'react'
import { track } from '../../../lib/analytics'
import { claimRationedTip, retireRationedTip } from '../../../lib/nuxTips'
import type { OneShotTipDismissReason } from '../../../components/OneShotTip'

const TIP = 'schedule_filter'
// Lands after the arrival label peek has started, so the chip is already
// labeled "Filter" when the tip points at it.
const DELAY_MS = 1200

/**
 * Festival mode's coachmark on the schedule filter chip. Rationed like the
 * clash tip (a few showings per device, once per session), and only claimed
 * while tapping the filter would visibly change the day on screen — the caller
 * decides that through `eligible`. Using the filter retires it for good.
 */
export function useFilterTip(eligible: boolean) {
  const [open, setOpen] = useState(false)
  const claimed = useRef(false)

  useEffect(() => {
    if (!eligible || claimed.current) return
    const id = window.setTimeout(() => {
      if (!claimRationedTip(TIP)) return
      claimed.current = true
      setOpen(true)
      track('nux_tip_shown', { tip: TIP })
    }, DELAY_MS)
    return () => window.clearTimeout(id)
  }, [eligible])

  const dismiss = useCallback(
    (via: OneShotTipDismissReason) => {
      if (!open) return
      setOpen(false)
      track('nux_tip_dismissed', { tip: TIP, via })
    },
    [open],
  )

  const retire = useCallback(() => {
    retireRationedTip(TIP)
    setOpen(false)
  }, [])

  return { open, dismiss, retire }
}
